'use client';
import { useEffect, useMemo, useState } from 'react';
import { c, ramp } from '@/lib/vster/theme';
import Icon from '../Icon';

interface Props { navigate: (s: string) => void; }

interface SavedItem {
  id: number;
  title: string;
  org: string;
  region: string;
  type: string;
  supportAmount: string | null;
  dday: string;
  daysLeft: number | null;
  applyStartDate: string | null;
  applyEndDate: string | null;
  urgent: boolean;
  closed: boolean;
  savedAt: string;
}

// 마감일 램프(디자인 토큰) → 배경 틴트 + AA 텍스트 색.
function ddColors(daysLeft: number | null): { bg: string; color: string } {
  const r = ramp(daysLeft);
  return { bg: `${r.c}22`, color: r.t };
}

// 저장된 마감일(applyEndDate)은 KST 마감시각을 UTC 로 표현한 값이므로,
//   +9h 후 UTC 날짜 요소를 읽어 KST 달력 날짜(연·월·일)를 안정적으로 얻는다(뷰어 타임존 무관).
function kstYmd(iso: string | null): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const k = new Date(t.getTime() + 9 * 3600_000);
  return { y: k.getUTCFullYear(), m: k.getUTCMonth(), d: k.getUTCDate() };
}
function todayKst(): { y: number; m: number; d: number } {
  const k = new Date(Date.now() + 9 * 3600_000);
  return { y: k.getUTCFullYear(), m: k.getUTCMonth(), d: k.getUTCDate() };
}

const WEEK = ['일', '월', '화', '수', '목', '금', '토'];

// 단건 .ics(캘린더 저장) data URI — 마감일을 종일 이벤트로.
function icsHref(it: SavedItem): string | null {
  const e = kstYmd(it.applyEndDate);
  if (!e) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const day = `${e.y}${pad(e.m + 1)}${pad(e.d)}`;
  const nextK = new Date(Date.UTC(e.y, e.m, e.d + 1));
  const dayEnd = `${nextK.getUTCFullYear()}${pad(nextK.getUTCMonth() + 1)}${pad(nextK.getUTCDate())}`;
  const esc = (s: string) => s.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//booster//saved//KO', 'BEGIN:VEVENT',
    `UID:saved-${it.id}@booster`, `DTSTART;VALUE=DATE:${day}`, `DTEND;VALUE=DATE:${dayEnd}`,
    `SUMMARY:[마감] ${esc(it.title)}`, `DESCRIPTION:${esc([it.org, it.region, it.supportAmount ?? ''].filter(Boolean).join(' · '))}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

export default function SavedScreen({ navigate }: Props) {
  const [items, setItems] = useState<SavedItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const today = useMemo(todayKst, []);
  const [ym, setYm] = useState<{ y: number; m: number }>({ y: today.y, m: today.m });
  const [sel, setSel] = useState<number | null>(null); // 선택된 날짜(일). 목록 연동 필터.

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/my/saved');
        if (res.status === 401 || (res.redirected && res.url.includes('/login'))) {
          if (alive) setErr('로그인이 필요합니다.'); return;
        }
        let data: { items?: SavedItem[]; error?: string } = {};
        try { data = await res.json(); } catch { if (alive) setErr('목록을 불러오지 못했습니다. (서버 재시작이 필요할 수 있습니다)'); return; }
        if (!res.ok) { if (alive) setErr(data.error ?? '목록을 불러오지 못했습니다.'); return; }
        if (alive) setItems(data.items ?? []);
      } catch {
        if (alive) setErr('목록을 불러오지 못했습니다.');
      }
    })();
    return () => { alive = false; };
  }, []);

  async function unsave(id: number) {
    setRemoving(id);
    try {
      const res = await fetch('/api/my/saved', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ docId: id }) });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.saved === false) setItems((prev) => prev ? prev.filter((p) => p.id !== id) : prev);
    } finally {
      setRemoving(null);
    }
  }

  const header = (n: number, urgent: number) => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', margin: 0, color: c.ink }}>
        찜한 공고 <span className="num" style={{ color: c.actionDark }}>{n}개</span>
      </h2>
      <span className="num" style={{ fontSize: 12.5, color: c.muted }}>
        마감 임박 <strong style={{ color: c.dangerText }}>{urgent}건</strong> · <Icon name="link" size={13} color={c.muted} /> 캘린더 + 목록 연동
      </span>
    </div>
  );

  if (err) return (
    <div>{header(0, 0)}
      <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, padding: 48, textAlign: 'center', color: c.ink }}>
        <Icon name="lock" size={36} color={c.muted} fill={1} />
        <p style={{ margin: '12px 0 16px', fontSize: 15 }}>{err}</p>
        <button onClick={() => navigate('home')} style={{ background: c.action, color: '#fff', border: 'none', cursor: 'pointer', padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: 'inherit' }}>홈으로</button>
      </div>
    </div>
  );

  if (items == null) return <div>{header(0, 0)}<div className="num" style={{ padding: 60, textAlign: 'center', color: c.muted, fontSize: 14 }}>불러오는 중…</div></div>;

  if (items.length === 0) return (
    <div>{header(0, 0)}
      <div onClick={() => navigate('list')} style={{ border: `1.5px dashed ${c.line}`, borderRadius: 16, padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', background: c.surface }}>
        <Icon name="bookmark_add" size={34} color={c.action} />
        <span style={{ fontSize: 16, fontWeight: 700, color: c.actionDark }}>아직 찜한 공고가 없어요</span>
        <span style={{ fontSize: 13, color: c.muted }}>지원사업 목록에서 하트를 눌러 관심공고를 등록하세요</span>
      </div>
    </div>
  );

  const urgent = items.filter((i) => i.urgent).length;

  return (
    <div>{header(items.length, urgent)}
      <style>{`
        .saved-wrap{display:grid;grid-template-columns:380px 1fr;gap:20px;align-items:start}
        @media(max-width:900px){.saved-wrap{grid-template-columns:1fr}}
      `}</style>
      <div className="saved-wrap">
        <SavedCalendar items={items} ym={ym} setYm={setYm} today={today} sel={sel} setSel={setSel} />
        <SavedList items={items} removing={removing} unsave={unsave} sel={sel} ym={ym} setSel={setSel} />
      </div>
    </div>
  );
}

// ── 좌: 캘린더 (찜 마감일 표시 · 날짜 클릭 → 목록 연동) ──
function SavedCalendar({ items, ym, setYm, today, sel, setSel }: {
  items: SavedItem[]; ym: { y: number; m: number }; setYm: (v: { y: number; m: number }) => void;
  today: { y: number; m: number; d: number }; sel: number | null; setSel: (d: number | null) => void;
}) {
  const byDay = useMemo(() => {
    const map = new Map<number, SavedItem[]>();
    for (const it of items) {
      const e = kstYmd(it.applyEndDate);
      if (!e || e.y !== ym.y || e.m !== ym.m) continue;
      const arr = map.get(e.d) ?? [];
      arr.push(it);
      map.set(e.d, arr);
    }
    return map;
  }, [items, ym]);
  const monthCount = useMemo(() => { let n = 0; byDay.forEach((a) => (n += a.length)); return n; }, [byDay]);

  const firstDow = new Date(Date.UTC(ym.y, ym.m, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(ym.y, ym.m + 1, 0)).getUTCDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const isToday = (d: number) => today.y === ym.y && today.m === ym.m && today.d === d;
  const move = (delta: number) => {
    const m = ym.m + delta;
    setYm({ y: ym.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 });
    setSel(null);
  };
  // 셀에 표시할 대표 최소 D-day 색(가장 임박한 것 기준)
  const dayColor = (arr: SavedItem[]) => ddColors(Math.min(...arr.map((i) => i.daysLeft ?? 9999)));

  return (
    <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={() => move(-1)} title="이전 달" style={navBtn}><Icon name="chevron_left" size={20} color={c.actionDark} /></button>
        <span className="num" style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>{ym.y}년 {ym.m + 1}월</span>
        <button onClick={() => move(1)} title="다음 달" style={navBtn}><Icon name="chevron_right" size={20} color={c.actionDark} /></button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 4 }}>
        {WEEK.map((w, i) => (
          <div key={w} className="num" style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: i === 0 ? c.dangerText : i === 6 ? c.actionDark : c.muted }}>{w}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
        {cells.map((d, idx) => {
          if (d == null) return <div key={idx} style={{ aspectRatio: '1', borderRadius: 8 }} />;
          const arr = byDay.get(d) ?? [];
          const has = arr.length > 0;
          const selected = sel === d;
          const dc = has ? dayColor(arr) : null;
          const dow = idx % 7;
          return (
            <button
              key={idx}
              onClick={() => has && setSel(selected ? null : d)}
              disabled={!has}
              title={has ? `${arr.length}건 마감` : undefined}
              style={{
                aspectRatio: '1', borderRadius: 8, position: 'relative', cursor: has ? 'pointer' : 'default',
                border: '1px solid ' + (selected ? c.action : isToday(d) ? c.actionBorder : 'transparent'),
                background: selected ? c.action : has ? (dc!.bg) : 'transparent',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: 0,
              }}>
              <span className="num" style={{
                fontSize: 13, fontWeight: isToday(d) || has ? 700 : 500,
                color: selected ? '#fff' : has ? dc!.color : (dow === 0 ? c.dangerText : dow === 6 ? c.actionDark : c.ink),
              }}>{d}</span>
              {has && <span style={{ width: 5, height: 5, borderRadius: '50%', background: selected ? '#fff' : dc!.color }} />}
            </button>
          );
        })}
      </div>

      <div className="num" style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${c.line}`, fontSize: 11.5, color: c.muted, lineHeight: 1.5 }}>
        <Icon name="event" size={13} color={c.muted} /> 이 달 마감 <strong style={{ color: c.actionDark }}>{monthCount}건</strong> · 날짜를 누르면 목록이 연동됩니다
      </div>
    </div>
  );
}

// ── 우: 찜 카드 목록 (선택 날짜 있으면 그 날 마감분만) ──
function SavedList({ items, removing, unsave, sel, ym, setSel }: {
  items: SavedItem[]; removing: number | null; unsave: (id: number) => void;
  sel: number | null; ym: { y: number; m: number }; setSel: (d: number | null) => void;
}) {
  const shown = useMemo(() => {
    if (sel == null) return items;
    return items.filter((it) => {
      const e = kstYmd(it.applyEndDate);
      return e && e.y === ym.y && e.m === ym.m && e.d === sel;
    });
  }, [items, sel, ym]);

  return (
    <div>
      {sel != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, background: c.actionSoft, border: `1px solid ${c.actionBorder}`, borderRadius: 10, padding: '8px 12px' }}>
          <Icon name="filter_alt" size={16} color={c.actionDark} fill={1} />
          <span style={{ fontSize: 13.5, fontWeight: 600, color: c.actionDark }}>{ym.m + 1}월 {sel}일 마감 {shown.length}건</span>
          <button onClick={() => setSel(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: c.actionDark, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, fontFamily: 'inherit' }}>
            전체 보기 <Icon name="close" size={14} color={c.actionDark} />
          </button>
        </div>
      )}

      {shown.length === 0 ? (
        <div style={{ background: c.surfaceAlt, borderRadius: 14, padding: 32, textAlign: 'center', color: c.muted, fontSize: 14 }}>이 날 마감되는 찜 공고가 없어요.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {shown.map((p) => {
            const dd = ddColors(p.daysLeft);
            const ics = icsHref(p);
            return (
              <div key={p.id} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10, boxShadow: '0 4px 16px -14px rgba(14,23,38,.14)', opacity: p.closed ? 0.7 : 1 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {p.type && <span className="num" style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: c.actionSoft, color: c.actionDark }}>{p.type}</span>}
                  <span className="num" style={{ fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: dd.bg, color: dd.color }}>{p.dday}</span>
                  <span className="num" style={{ marginLeft: 'auto', fontSize: 11, color: c.muted }}>{p.savedAt?.slice(0, 10)} 찜</span>
                </div>
                <a href={`/programs/${p.id}`} style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.3, margin: 0, color: c.ink, textDecoration: 'none' }}>{p.title}</a>
                <span className="num" style={{ fontSize: 12, color: c.actionDark }}>
                  {/* 지원금(supportAmount)이 너무 길면 30자에서 잘라 목록 한 줄이 깨지지 않게 한다. */}
                  {[p.org, p.region, p.supportAmount && p.supportAmount.length > 30 ? p.supportAmount.slice(0, 30).trimEnd() + '…' : p.supportAmount].filter(Boolean).join(' · ')}
                  {p.applyEndDate && <> · <b>마감 {p.applyEndDate.slice(5, 10).replace('-', '.')}</b></>}
                </span>
                <div style={{ display: 'flex', gap: 6, borderTop: `1px solid ${c.line}`, paddingTop: 12, flexWrap: 'wrap' }}>
                  <button onClick={() => unsave(p.id)} disabled={removing === p.id} style={miniBtn}>
                    <Icon name="favorite" size={15} color={c.dangerText} fill={1} /> 찜 해제
                  </button>
                  {ics && (
                    <a href={ics} download={`${p.title.slice(0, 40)}.ics`} style={miniBtn}>
                      <Icon name="calendar_add_on" size={15} color={c.muted} /> 캘린더 저장
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const navBtn: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 9, border: `1px solid ${c.line}`, background: c.surface,
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const miniBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', border: `1px solid ${c.line}`, background: c.surface, color: c.muted, textDecoration: 'none', fontFamily: 'inherit',
};
